import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Button } from './ui/button';

const GallerySection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  const gallery = [
    {
      image: 'https://customer-assets.emergentagent.com/job_zeropower/artifacts/kqqz9ndk_IMG_2522.jpg',
      location: 'Bhubaneswar, Odisha',
      description: 'Residential rooftop solar installation'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_zeropower/artifacts/gxp3bjwa_IMG_2523.jpg',
      location: 'Bhubaneswar, Odisha',
      description: 'Ground-mounted solar system'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_zeropower/artifacts/hc6je02i_IMG_2524.jpg',
      location: 'Kuspangi, Odisha',
      description: 'Commercial rooftop installation'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_zeropower/artifacts/25njb5ir_IMG_2527.jpg',
      location: 'Budhangadeuli, Odisha',
      description: 'Solar panels on residential property'
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_zeropower/artifacts/czva2wzv_IMG_2528.jpg',
      location: 'Angul, Odisha',
      description: 'Happy customer with solar installation'
    }
  ];

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-900 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-orange-500">Recent Projects</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            See our successful solar installations across Odisha
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {gallery.map((item, index) => (
                <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
                  <div className="relative group">
                    <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl">
                      <img
                        src={item.image}
                        alt={item.description}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    {/* Image Info */}
                    <div className="absolute bottom-6 left-6 right-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                        <div className="flex items-center space-x-2 mb-2">
                          <MapPin className="w-5 h-5 text-orange-400" />
                          <span className="font-bold text-lg">{item.location}</span>
                        </div>
                        <p className="text-blue-100">{item.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <Button
            onClick={scrollPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-orange-500 text-gray-900 hover:text-white rounded-full w-12 h-12 p-0 shadow-2xl z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button
            onClick={scrollNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-orange-500 text-gray-900 hover:text-white rounded-full w-12 h-12 p-0 shadow-2xl z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </Button>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-3 mt-8">
            {gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === selectedIndex
                    ? 'bg-orange-500 w-8 h-3'
                    : 'bg-gray-300 w-3 h-3 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Stats Below Gallery */}
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">500+</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Projects Completed</h3>
            <p className="text-gray-600">Across all districts of Odisha</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">100%</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Customer Satisfaction</h3>
            <p className="text-gray-600">4.9/5 average rating</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">7-10</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Days Installation</h3>
            <p className="text-gray-600">Quick and professional service</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
